import _ from 'lodash';

import { useAuth } from '@/composables';
import { useInitializeData } from './use-initialize-app-launcher';
import {
  useAppLauncherStore,
  useAppLauncherSetters,
} from './use-app-launcher-store';
import { useAppLauncherRouting } from './use-app-launcher-routing';
import { AppLauncherSettings } from '../types';
import { SDK, APP_KEYS, APP_STATES } from '../constants';

type CheckStatusRate = 'update-in-progress' | 'background';
const context = {
  rate: 'update-in-progress' as CheckStatusRate,
  timerId: undefined as number | undefined,
  isChecking: false,
};

/**
 * When admin changes HITL version or starts an update process
 * we kick all active users and wait for an update to complete.
 */
export const useAppUpdateTracking = () => {
  const CHECK_UPDATE_STATUS_INTERVAL = 10 * 1000;
  const CHECK_UPDATE_STATUS_BACKGROUND_INTERVAL = 3 * 60 * 1000;
  const COMPLETE_UPDATE_TIMEOUT = 1000;

  const store = useAppLauncherStore();
  const setters = useAppLauncherSetters();
  const { goTo, goToAppBundle, goToAppLauncher } = useAppLauncherRouting();
  const { getAPIAppLauncherSettings, saveAppLauncherSettings } = useInitializeData();
  const { getToken } = useAuth();

  /**
   * During update process we should check status more often.
   * When user is using Agent UI we can check updates at a lower rate.
   */
  const startUpdateTracking = (options?: { rate?: CheckStatusRate; }) => {
    const { rate = 'update-in-progress' } = options || {};
    if (!store.canUserInteractWithHITL) {
      return;
    }

    context.rate = rate;
    const delay = rate === 'update-in-progress'
      ? CHECK_UPDATE_STATUS_INTERVAL
      : CHECK_UPDATE_STATUS_BACKGROUND_INTERVAL;

    context.timerId = window.setInterval(async () => {
      if (context.isChecking) {
        return;
      }

      context.isChecking = true;
      await checkUpdateStatus();
      context.isChecking = false;
    }, delay);
  };

  const stopUpdateTracking = () => {
    window.clearInterval(context.timerId);
    context.rate = 'update-in-progress';
    context.timerId = undefined;
    context.isChecking = false;
  };

  const changeTrackingRate = (options: { rate: CheckStatusRate; }) => {
    if (context.rate !== options.rate) {
      stopUpdateTracking();
      startUpdateTracking(options);
    }
  };

  /**
   * Depending on update or version status redirect user to corresponding routes.
   * Increase tracking rate during update process and decrease it in all other cases.
   */
  const checkUpdateStatus = async () => {
    if (!getToken()) {
      return;
    }

    try {
      const [status, settings] = await Promise.all([
        SDK.HITL.migrations.getRunMigrationsStatus(),
        store.canUseSettingsAPI ? getAPIAppLauncherSettings() : _.noop(),
      ]);

      /* If there is no update in progress - check if HITL app version was changed */
      if (_.isEmpty(status)) {
        const { appVersion = '' } = settings || {};
        changeTrackingRate({ rate: 'background' });

        /* If app version is not available or is the same - continue checking */
        if (!appVersion || appVersion === store.settingsInDB.appVersion) {
          return;
        }

        return onVersionChange(settings as AppLauncherSettings);
      }

      const { complete, remaining: pending, reports, meta, error } = status;
      setters.changeUpdateProgress({ complete, pending, reports, meta, error });

      if (!_.isEmpty(error)) {
        changeTrackingRate({ rate: 'background' });
        const state = store.isUserAdmin
          ? APP_STATES.DATA_UPDATE_ERROR_ADMIN
          : APP_STATES.DATA_UPDATE_ERROR_AGENT;

        return goTo({ state });
      }

      if (store.isUpdateInProgress) {
        changeTrackingRate({ rate: 'update-in-progress' });
        return goTo({ state: APP_STATES.APP_IS_UPDATING });
      }

      /* On update complete show results while app is deciding what to do next */
      if (store.isUpdateComplete) {
        changeTrackingRate({ rate: 'background' });
        goTo({ state: APP_STATES.APP_IS_UPDATING });

        return completeDataUpdate();
      }
    } catch (e) {
      /* Skip iteration on error */
      console.error('Error while checking update status!', e);
    }
  };

  /** Display a 100% progress before completing update and clear migrations lock */
  const completeDataUpdate = async () => {
    const complete = store.update.complete.concat(store.update.pending);
    setters.changeUpdateProgress({ complete, pending: [], isInProgress: true });

    return new Promise<void>(resolve =>
      setTimeout(async () => {
        await onUpdateSuccess();
        resolve();
      }, COMPLETE_UPDATE_TIMEOUT)
    );
  };

  const onVersionChange = async (settings: AppLauncherSettings) => {
    setters.setAppSettings(settings);

    /* Basic agents are redirected to Agent UI as soon as possible */
    if (store.isUserBasicAgent) {
      return goToAppBundle({ app: APP_KEYS.AGENT_UI });
    }

    /* Admins are also redirected to Agent UI if they are working as agents */
    if (store.isUserAdmin && store.component) {
      return goToAppBundle({ app: APP_KEYS.AGENT_UI });
    }

    /* Otherwise admins are redirected to settings page */
    if (store.isUserAdmin) {
      return goToAppLauncher();
    }
  };

  const onUpdateSuccess = async () => {
    const appVersion = store.updateTargetVersion;

    if (!appVersion) {
      console.warn('Update should have a target version!', store.update);
      setters.applyUpdateChanges();
      return goTo({ state: APP_STATES.NO_AGENT_ACCESS });
    }

    /* Basic agents are redirected to Agent UI as soon as possible */
    if (store.isUserBasicAgent) {
      setters.applyUpdateChanges();
      return goToAppBundle({ app: APP_KEYS.AGENT_UI });
    }

    /* Admins should save version in the DB whenever possible */
    if (store.isUserAdmin) {
      await saveAppLauncherSettings({ appVersion });
      await SDK.HITL.migrations.deleteMigrationsTableLock();
      setters.applyUpdateChanges();
    }

    /* Admins are also redirected to Agent UI if they are working as agents */
    if (store.isUserAdmin && (store.component || store.canSkipLauncher)) {
      return goToAppBundle({ app: APP_KEYS.AGENT_UI });
    }

    /* Otherwise admins are redirected to settings page */
    if (store.isUserAdmin) {
      return goToAppLauncher();
    }
  };

  return {
    startUpdateTracking,
    changeTrackingRate,
    checkUpdateStatus,
    stopUpdateTracking,
  };
};
